import React, { useState, useEffect } from 'react';
import { AppTab, AuthUser } from './types';
import { api } from './api';
import { tg } from './telegram';
import { DashboardScreen } from './screens/DashboardScreen';
import { GoodsInwardScreen } from './screens/GoodsInwardScreen';
import { StockTransferScreen } from './screens/StockTransferScreen';
import { ShiftReconcileScreen } from './screens/ShiftReconcileScreen';
import { WasteScreen } from './screens/WasteScreen';
import { InventoryBalanceScreen } from './screens/InventoryBalanceScreen';
import { TimelineScreen } from './screens/TimelineScreen';
import { LoginScreen } from './screens/LoginScreen';
import { MyPageScreen } from './screens/MyPageScreen';
import { MasterAdminScreen } from './screens/MasterAdminScreen';
import { PrintedTemplateModal } from './components/PrintedTemplateModal';
import { LogoutConfirmModal } from './components/LogoutConfirmModal';
import {
  LayoutDashboard,
  Package,
  Truck,
  CheckCircle2,
  History,
  Coffee,
  Trash2,
  ShieldCheck,
  UserCheck,
  User,
  LogOut,
  Loader2,
  ArrowLeft,
  SlidersHorizontal,
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';

export const App: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  const getTabFromUrl = (): AppTab => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab && ['DASHBOARD', 'INWARD', 'TRANSFER', 'RECONCILE', 'WASTE', 'BALANCE', 'TIMELINE', 'MY_PAGE', 'MASTER_ADMIN'].includes(tab)) {
      return tab as AppTab;
    }
    return 'DASHBOARD';
  };

  const [currentTab, setCurrentTab] = useState<AppTab>(getTabFromUrl);

  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  useEffect(() => {
    // Listen for browser Back/Forward buttons
    const handlePopState = () => {
      setCurrentTab(getTabFromUrl());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    // Initialize Telegram WebApp environment
    tg.ready();
    const app = tg.getApp();
    if (app) {
      app.setHeaderColor('#F8F5F0');
      app.setBackgroundColor('#F8F5F0');
    }

    // Verify existing token session
    const initAuth = async () => {
      const token = api.getToken();
      if (token) {
        try {
          const user = await api.getCurrentUser();
          setCurrentUser(user);
        } catch {
          api.logout();
          setCurrentUser(null);
        }
      }
      setIsAuthLoading(false);
    };

    initAuth();

    const handleUnauthorized = () => {
      setCurrentUser(null);
      handleTabChange('DASHBOARD');
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener('auth:logout', handleUnauthorized);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('auth:logout', handleUnauthorized);
    };
  }, []);

  useEffect(() => {
    if (currentTab !== 'DASHBOARD') {
      tg.backButton.show(() => {
        handleTabChange('DASHBOARD');
      });
    } else {
      tg.backButton.hide();
    }
  }, [currentTab]);

  const handleTabChange = (tab: AppTab) => {
    // Role protection for INWARD
    if (tab === 'INWARD' && currentUser?.role === 'STAFF') {
      tg.haptic.warning();
      toast.error('Nghiệp vụ Nhập hàng Kho Tổng (L1) chỉ dành cho Quản lý / Admin');
      return;
    }

    tg.haptic.selectionChanged();
    setCurrentTab(tab);

    // Update browser URL query param (?tab=...) without full page reload
    const url = new URL(window.location.href);
    if (tab === 'DASHBOARD') {
      url.searchParams.delete('tab');
    } else {
      url.searchParams.set('tab', tab);
    }
    window.history.pushState({}, '', url.toString());

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRequestLogout = () => {
    tg.haptic.impact('light');
    setIsLogoutModalOpen(true);
  };

  const handleConfirmLogout = () => {
    setIsLogoutModalOpen(false);
    tg.haptic.warning();
    api.logout();
    toast.success('Đã đăng xuất');
  };

  if (isAuthLoading) {
    return (
      <div className="app-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
          <Loader2 size={36} className="animate-spin" color="var(--primary-accent)" style={{ margin: '0 auto 12px auto' }} />
          <div style={{ fontSize: 14, fontWeight: 700 }}>Đang kiểm tra phiên đăng nhập...</div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="app-container">
        <Toaster position="top-center" toastOptions={{ duration: 3000, style: { fontWeight: 'bold' } }} />
        <LoginScreen onLoginSuccess={(user) => setCurrentUser(user)} />
      </div>
    );
  }

  const isStaff = currentUser.role === 'STAFF';
  const isMasterAdmin = currentTab === 'MASTER_ADMIN';

  return (
    <div className={`app-container ${isMasterAdmin ? 'desktop-mode' : ''}`}>
      <Toaster
        position="top-center"
        containerStyle={{ top: 56 }}
        toastOptions={{ duration: 3000, style: { fontWeight: 'bold' } }}
      />

      {/* App Header */}
      <header className="app-header">
        {currentTab === 'MY_PAGE' || currentTab === 'MASTER_ADMIN' ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <button
              onClick={() => handleTabChange('DASHBOARD')}
              className="btn-header-back"
              title="Quay lại Tổng Quan"
            >
              <ArrowLeft size={16} />
              <span>Quay Lại Tổng Quan</span>
            </button>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>
              {currentTab === 'MY_PAGE' ? 'Tài Khoản Cá Nhân' : 'Màn Hình Quản Trị Master'}
            </div>
            <button
              onClick={handleRequestLogout}
              className="btn-header-back"
              style={{ color: '#DC2626', borderColor: '#FEE2E2', backgroundColor: '#FFF5F5' }}
              title="Đăng xuất"
            >
              <LogOut size={15} />
              <span>Thoát</span>
            </button>
          </div>
        ) : (
          <>
            <div className="brand-badge" style={{ flexShrink: 0 }}>
              <div className="brand-icon">
                <Coffee size={18} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div className="brand-name">Bar Ingredients</div>
                <div className="brand-role">Quản lý kho nguyên liệu & Quầy Bar</div>
              </div>
            </div>

            {/* User Identity, My Page & Logout */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
              <button
                onClick={() => handleTabChange('MY_PAGE')}
                className={`badge ${isStaff ? 'badge-success' : 'badge-warning'}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  padding: '4px 7px',
                  cursor: 'pointer',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 11,
                }}
                title="Bấm để mở Trang cá nhân (Đổi tên, Đổi mật khẩu)"
              >
                {isStaff ? <UserCheck size={12} /> : <ShieldCheck size={12} />}
                <span style={{ fontWeight: 700 }}>{isStaff ? 'Staff' : 'Admin'}</span>
              </button>

              <button
                onClick={() => handleTabChange('MY_PAGE')}
                style={{
                  background: 'none',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '5px 7px',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title="Trang cá nhân (Đổi tên & Mật khẩu)"
              >
                <User size={14} />
              </button>

              {currentUser?.role === 'ADMIN' && (
                <button
                  onClick={() => handleTabChange('MASTER_ADMIN')}
                  style={{
                    background: '#FAF7F2',
                    border: '1px solid #8A644D',
                    borderRadius: 'var(--radius-sm)',
                    padding: '5px 8px',
                    color: '#3C2114',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: '11px',
                    fontWeight: 800,
                  }}
                  title="Màn hình Quản trị Master (Công thức & Kho)"
                >
                  <SlidersHorizontal size={13} />
                  <span className="hide-mobile">Master</span>
                </button>
              )}

              <button
                onClick={handleRequestLogout}
                style={{
                  background: 'none',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '6px 8px',
                  color: 'var(--text-tertiary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
                title="Đăng xuất"
              >
                <LogOut size={15} />
              </button>
            </div>
          </>
        )}
      </header>

      {/* Main Screen Content */}
      <main className="main-content">
        {currentTab === 'DASHBOARD' && (
          <DashboardScreen
            onNavigate={handleTabChange}
            onOpenPrintModal={() => setIsPrintModalOpen(true)}
            currentUser={currentUser}
          />
        )}

        {currentTab === 'INWARD' && (
          <GoodsInwardScreen
            onBack={() => handleTabChange('DASHBOARD')}
            onSuccess={() => handleTabChange('DASHBOARD')}
          />
        )}

        {currentTab === 'TRANSFER' && (
          <StockTransferScreen
            onBack={() => handleTabChange('DASHBOARD')}
            onSuccess={() => handleTabChange('DASHBOARD')}
          />
        )}

        {currentTab === 'RECONCILE' && (
          <ShiftReconcileScreen
            onBack={() => handleTabChange('DASHBOARD')}
            onSuccess={() => handleTabChange('DASHBOARD')}
          />
        )}

        {currentTab === 'WASTE' && (
          <WasteScreen
            onBack={() => handleTabChange('DASHBOARD')}
            onSuccess={() => handleTabChange('DASHBOARD')}
          />
        )}

        {currentTab === 'BALANCE' && (
          <InventoryBalanceScreen
            onBack={() => handleTabChange('DASHBOARD')}
            onSuccess={() => handleTabChange('DASHBOARD')}
          />
        )}

        {currentTab === 'TIMELINE' && <TimelineScreen />}

        {currentTab === 'MY_PAGE' && (
          <MyPageScreen
            currentUser={currentUser}
            onBack={() => handleTabChange('DASHBOARD')}
            onUserUpdated={(updatedUser) => setCurrentUser(updatedUser)}
            onLogout={handleRequestLogout}
          />
        )}

        {currentTab === 'MASTER_ADMIN' && (
          <MasterAdminScreen />
        )}
      </main>


      {/* Bottom Navigation Bar */}
      <nav className="bottom-nav">
        <button
          className={`nav-item ${currentTab === 'DASHBOARD' ? 'active' : ''}`}
          onClick={() => handleTabChange('DASHBOARD')}
        >
          <LayoutDashboard size={20} />
          <span>Tổng Quan</span>
        </button>

        {isStaff ? (
          <button
            className={`nav-item ${currentTab === 'WASTE' ? 'active' : ''}`}
            onClick={() => handleTabChange('WASTE')}
          >
            <Trash2 size={20} />
            <span>Hủy Hỏng</span>
          </button>
        ) : (
          <button
            className={`nav-item ${currentTab === 'INWARD' ? 'active' : ''}`}
            onClick={() => handleTabChange('INWARD')}
          >
            <Package size={20} />
            <span>Nhập Hàng</span>
          </button>
        )}

        <button
          className={`nav-item ${currentTab === 'TRANSFER' ? 'active' : ''}`}
          onClick={() => handleTabChange('TRANSFER')}
        >
          <Truck size={20} />
          <span>Chuyển Kho</span>
        </button>

        <button
          className={`nav-item ${currentTab === 'RECONCILE' ? 'active' : ''}`}
          onClick={() => handleTabChange('RECONCILE')}
        >
          <CheckCircle2 size={20} />
          <span>Chốt Ca</span>
        </button>

        <button
          className={`nav-item ${currentTab === 'TIMELINE' ? 'active' : ''}`}
          onClick={() => handleTabChange('TIMELINE')}
        >
          <History size={20} />
          <span>Nhật Ký</span>
        </button>
      </nav>

      {/* Printed-Template Modal */}
      <PrintedTemplateModal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
      />

      {/* Logout Confirmation Modal */}
      <LogoutConfirmModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleConfirmLogout}
        user={currentUser}
      />
    </div>
  );
};

export default App;
